import { Traject } from "@/entities/dossier-details";
import { ReikwijdteGewijzigdActieDto } from "@/shared/types";
import { parseISO } from "date-fns";
import { ActieDtoTransformatieError } from "./actie-error";
import { BaseActie } from "./base-actie";

export class ReikwijdteGewijzigdActieTransformatieError extends ActieDtoTransformatieError {
    private constructor(reason: string) {
        super("ReikwijdteGewijzigd", "Gewijzigd", reason);
    }

    public static geenNieuweWaardeVoorGewijzigd(): ReikwijdteGewijzigdActieTransformatieError {
        return new ReikwijdteGewijzigdActieTransformatieError(`nieuweWaarde vereist is, maar deze is: nieuweWaarde=null`);
    }
}

export type ReikwijdteGewijzigdActie = BaseActie & {
    type: "ReikwijdteGewijzigd";
    nieuweWaarde: Traject[];
    actieTrigger: "SpoorwebLocatieWijziging" | "SpoorwebGekoppeldAanDossier";
};

export const ReikwijdteGewijzigdActie = {
    fromDto: (dto: ReikwijdteGewijzigdActieDto): ReikwijdteGewijzigdActie => {
        if (!dto.nieuweWaarde) {
            throw ReikwijdteGewijzigdActieTransformatieError.geenNieuweWaardeVoorGewijzigd();
        }

        return {
            id: dto.id,
            type: "ReikwijdteGewijzigd",
            timelineContent: {
                titel: "Reikwijdte wijzigen",
                beschrijving:
                    dto.actieTrigger === "SpoorwebLocatieWijziging"
                        ? "Omdat de incidentlocatie in Spoorweb gewijzigd is"
                        : "Omdat een Spoorweb-incident aan het dossier gekoppeld is",
                actieTekst: "Controleer en bevestig reikwijdte",
            },
            dialogContent: {
                titel: "Update reikwijdte",
                beschrijving:
                    dto.actieTrigger === "SpoorwebLocatieWijziging"
                        ? "De incidentlocatie in Spoorweb is gewijzigd, dit leidt tot andere trajecten. Wil je deze trajecten overnemen als reikwijdte in het SEIN dossier?"
                        : "Er is een Spoorweb-incident aan het dossier gekoppeld. Wil je de trajecten van dit incident overnemen als reikwijdte in het SEIN dossier?",
                weigerenTitel: "Update reikwijdte weigeren?",
                weigerenBeschrijving: "Weet je zeker dat je de update van de reikwijdte wilt weigeren?",
                weigerenActieTekst: "Reikwijdte weigeren",
            },
            verstuurdOpDatumTijd: parseISO(dto.verstuurdOpDatumTijd),
            toelichting: dto.toelichting,
            status: dto.status,
            wijzigingType: dto.wijzigingType,
            nieuweWaarde: dto.nieuweWaarde.map(Traject.fromDto),
            actieTrigger: dto.actieTrigger,
        };
    },
    update: (actie: ReikwijdteGewijzigdActie, trajecten: Traject[]): ReikwijdteGewijzigdActie => {
        return { ...actie, nieuweWaarde: trajecten };
    },
    toDto: (actie: ReikwijdteGewijzigdActie): ReikwijdteGewijzigdActieDto => ({
        id: actie.id,
        type: actie.type,
        verstuurdOpDatumTijd: actie.verstuurdOpDatumTijd.toISOString(),
        status: actie.status,
        toelichting: actie.toelichting,
        nieuweWaarde: actie.nieuweWaarde as ReikwijdteGewijzigdActieDto["nieuweWaarde"],
        wijzigingType: actie.wijzigingType,
        actieTrigger: actie.actieTrigger,
    }),
};
